import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import api from "../../api/axiosConfig";
import { format, differenceInDays } from "date-fns";
import * as XLSX from "xlsx";

const TimesheetBarChart = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [xAxisLabel, setXAxisLabel] = useState("Day");
  const [selectedMode, setSelectedMode] = useState("auto"); // "auto" | "daily" | "weekly" | "monthly"

  useEffect(() => {
    if (!startDate || !endDate) {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      setStartDate(start);
      setEndDate(today);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, selectedMode]);

  const fetchData = async () => {
    try {
      if (!startDate || !endDate) return;

      const daysDifference = differenceInDays(endDate, startDate);
      let mode = "daily";
      let label = "Day";

      if (selectedMode !== "auto") {
        mode = selectedMode;
        label = selectedMode.charAt(0).toUpperCase() + selectedMode.slice(1);
      } else {
        if (daysDifference > 7 && daysDifference <= 31) {
          mode = "weekly";
          label = "Week";
        } else if (daysDifference > 31 && daysDifference <= 366) {
          mode = "monthly";
          label = "Month";
        } else if (daysDifference > 366) {
          mode = "yearly";
          label = "Year";
        }
      }

      setXAxisLabel(label);

      const url = `/api/hours-all/${mode}/${format(
        startDate,
        "yyyy-MM-dd"
      )}/${format(endDate, "yyyy-MM-dd")}`;

      const response = await api.get(url);

      const filteredData = (
        Array.isArray(response.data) ? response.data : []
      ).filter(
        (item) =>
          convertToHours(item.nonProductiveHours) > 0 ||
          convertToHours(item.productiveHours) > 0 ||
          convertToHours(item.vo_plannedHours) > 0 ||
          convertToHours(item.plannedHoursForEstimation) > 0
      );

      setChartData(filteredData);
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setSelectedMode("auto");
    if (endDate && date > endDate) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (date) => {
    setSelectedMode("auto");
    if (startDate && date < startDate) {
      alert("End date cannot be before start date.");
      return;
    }
    setEndDate(date);
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedMode("auto");
  };

  const isSameMonthRange = (start, end) =>
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth();

  const convertToHours = (timeValue) => {
    if (!timeValue) return 0;

    if (typeof timeValue === "string" && timeValue.includes(":")) {
      const [hours, minutes] = timeValue.split(":").map(Number);
      return Math.round(hours + minutes / 60);
    }

    if (typeof timeValue === "number") {
      return Math.round(timeValue);
    }

    return 0;
  };

  const getXAxisLabels = () => {
    return chartData.map((item) =>
      item.year ? item.year.toString() : item.day
    );
  };

  const seriesData = [
    {
      name: "Non-Productive",
      type: "bar",
      data: chartData.map((d) => convertToHours(d.nonProductiveHours)),
      label: { show: true, position: "top", color: "#000", fontSize: 12 },
    },
    {
      name: "Productive",
      type: "bar",
      data: chartData.map((d) => convertToHours(d.productiveHours)),
      label: { show: true, position: "top", color: "#000", fontSize: 12 },
    },
    {
      name: "VO",
      type: "bar",
      data: chartData.map((d) => convertToHours(d.vo_plannedHours)),
      label: { show: true, position: "top", color: "#000", fontSize: 12 },
    },
    {
      name: "Project Secured",
      type: "bar",
      data: chartData.map((d) => convertToHours(d.plannedHoursForEstimation)),
      label: { show: true, position: "top", color: "#000", fontSize: 12 },
    },
  ];

  const options = {
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: {
      data: ["Non-Productive", "Productive", "VO", "Project Secured"],
    },
    grid: { left: "3%", right: "4%", bottom: "8%", containLabel: true },
    xAxis: {
      type: "category",
      data: getXAxisLabels(),
      axisLabel: { fontSize: 12, color: "#000", rotate: 90 },
      name: xAxisLabel,
      nameLocation: "middle",
      nameGap: 80,
    },
    yAxis: {
      type: "value",
      axisLabel: { fontSize: 13, color: "#000" },
      name: "Hours",
      nameLocation: "middle",
      nameGap: 40,
    },
    series: seriesData,
  };

  const exportToExcel = () => {
    if (chartData.length === 0) {
      alert("No data available to export.");
      return;
    }

    const ws_data = chartData.map((data) => [
      data.day,
      data.nonProductiveHours,
      data.productiveHours,
      data.vo_plannedHours,
      data.plannedHoursForEstimation,
    ]);

    const ws = XLSX.utils.aoa_to_sheet([
      [
        xAxisLabel,
        "Non Productive Hours",
        "Productive Hours",
        "VO Planned Hours",
        "Planned Hours for Estimation",
      ],
      ...ws_data,
    ]);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Raw Data");

    const chartDataFormatted = [
      [xAxisLabel, "Actual Hours", "Selling Hours"],
      ...chartData.map((item) => [
        item.day,
        convertToHours(item.nonProductiveHours) +
          convertToHours(item.productiveHours),
        convertToHours(item.plannedHoursForEstimation) +
          convertToHours(item.vo_plannedHours),
      ]),
    ];

    const chartWs = XLSX.utils.aoa_to_sheet(chartDataFormatted);
    XLSX.utils.book_append_sheet(wb, chartWs, "Actual Vs Selling");

    XLSX.writeFile(wb, "Timesheet_Data_with_Chart.xlsx");
  };

  return (
    <div className="project-bar-p-vs-np-report">
      <div className="report-date-con">
        <label>Start Date: </label>
        <DatePicker
          className="project-barchart-datepicker"
          dateFormat={"dd-MM-yyyy"}
          selected={startDate}
          onChange={handleStartDateChange}
          placeholderText="Select Start Date"
        />

        <label>End Date: </label>
        <DatePicker
          className="project-barchart-datepicker"
          dateFormat={"dd-MM-yyyy"}
          selected={endDate}
          onChange={handleEndDateChange}
          placeholderText="Select End Date"
        />

        <button onClick={handleClear} className="chart-clear-btn">
          Clear
        </button>
        <button onClick={exportToExcel} className="chart-export-btn">
          Export to Excel
        </button>

        {startDate && endDate && isSameMonthRange(startDate, endDate) && (
          <>
            <label style={{ marginLeft: "1rem" }}>Mode: </label>
            <select
              value={selectedMode}
              onChange={(e) => setSelectedMode(e.target.value)}
              className="chart-mode-select"
            >
              <option value="auto">Auto</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </>
        )}
      </div>
      <ReactECharts
        option={options}
        style={{ height: "500px", width: "100%" }}
      />
    </div>
  );
};

export default TimesheetBarChart;
